import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../api/client';
import { API_CONFIG } from '../api/config';

interface User {
  username: string;
  userId: number;
  base64EncodedAuthenticationKey?: string;
  authenticated: boolean;
  officeId?: number;
  officeName?: string;
  roles?: string[];
  permissions?: string[];
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token') || localStorage.getItem('access_token'));
  const [user, setUser] = useState<User | null>(null);

  const login = async (username: string, password: string) => {
    // Create Basic Auth token
    const authToken = btoa(`${username}:${password}`);
    
    try {
      const response = await api.post('/authentication', {}, {
        headers: {
          ...API_CONFIG.HEADERS,
          'Authorization': `Basic ${authToken}`
        }
      });

      setToken(authToken);
      setUser(response.data);
      localStorage.setItem('token', authToken);
      // Also store in the main auth system keys for consistency
      localStorage.setItem('access_token', authToken);
      
      // Set the token in axios defaults
      api.defaults.headers.common['Authorization'] = `Basic ${authToken}`;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_info');
    delete api.defaults.headers.common['Authorization'];
  };

  // Check token validity and fetch user data on mount
  // Note: This AuthContext appears to be legacy. The main authentication uses /api/auth/login
  // Disabling automatic token validation to prevent unwanted logouts
  useEffect(() => {
    // Only validate if we have a token but no user, and only once on mount
    // Don't auto-validate on every token change to prevent session issues
    const validateToken = async () => {
      if (token && !user) {
        try {
          // Try to decode token to get user info instead of making API call
          // This prevents unnecessary API calls that might cause session issues
          const storedUserInfo = localStorage.getItem('user_info');
          if (storedUserInfo) {
            try {
              const userData = JSON.parse(storedUserInfo);
              setUser(userData);
            } catch (e) {
              // If parsing fails, don't logout - just leave user as null
              console.warn('Could not parse stored user info');
            }
          }
        } catch (error) {
          // Don't auto-logout on validation failure - let the main auth system handle it
          console.warn('Token validation skipped to prevent session issues');
        }
      }
    };

    // Only run once on mount, not on every token change
    if (token && !user) {
      validateToken();
    }
  }, []); // Empty dependency array - only run once

  return (
    <AuthContext.Provider value={{ 
      token, 
      user, 
      login, 
      logout, 
      isAuthenticated: !!token && !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 